import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  Box,
  Button,
  Typography,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  Divider,
  ListSubheader,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import { SlideBlockView } from "./SlideBlockView";
import { BlockEditor } from "./BlockEditor";
import type {
  CanvasSlideBlock,
  CanvasElement,
  ReportBlock,
  ReportFilters,
  ChartBlock,
  HeadingBlock,
  NarrativeBlock,
} from "../api/types";

const CANVAS_W = 1280;
const CANVAS_H = 720;
const CANVAS_BG = "#0B0F14";

interface CanvasSlideEditorProps {
  block: CanvasSlideBlock;
  clientId: string | null;
  open: boolean;
  onClose: () => void;
  onChange: (block: CanvasSlideBlock) => void;
}

// Lightweight unique id
const newId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `canvas-el-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const SUMMARY_METRICS = [
  "sleep_performance",
  "sleep_duration",
  "recovery_score",
  "strain",
  "resting_heart_rate",
  "hrv",
];
const BREAKDOWN_METRICS = [
  "sleep_score",
  "recovery_score",
  "strain",
  "resting_heart_rate",
  "hrv",
];

function makeCanvasChildBlock(
  type: ReportBlock["type"],
  filters: ReportFilters,
  id: string,
): ReportBlock {
  switch (type) {
    case "chart":
      return {
        id,
        type: "chart",
        title: "",
        chartKind: "line",
        metric: "recovery_score",
        granularity: "daily",
        windowDays: 90,
      } as ChartBlock;
    case "heading":
      return { id, type: "heading", text: "Heading" } as HeadingBlock;
    case "narrative":
      return { id, type: "narrative", text: "" } as NarrativeBlock;
    case "kpiCards":
      return {
        id,
        type: "kpiCards",
        title: "Key Metrics",
        metrics: BREAKDOWN_METRICS,
        filters,
      };
    case "breakdown":
      return {
        id,
        type: "breakdown",
        title: "Breakdown",
        dimension: "department",
        metrics: BREAKDOWN_METRICS,
        filters,
      };
    case "segmentComparison":
      return {
        id,
        type: "segmentComparison",
        title: "Segment Comparison",
        metric: "recovery_score",
        chartKind: "line" as const,
        granularity: "daily" as const,
        filters,
        segments: [
          { label: "Segment A", filterOverride: {} },
          { label: "Segment B", filterOverride: {} },
        ],
      };
    case "comparison":
      return {
        id,
        type: "comparison",
        title: "Metric Comparison",
        metricA: "recovery_score",
        metricB: "sleep_performance",
        viewMode: "trend",
        segmentBy: "department",
        filters,
      };
    case "weatherImpact":
      return {
        id,
        type: "weatherImpact",
        title: "Weather Impact",
        metric: "recovery_score",
        filters,
      };
    case "image":
      return { id, type: "image", url: "", caption: "", fit: "contain" };
    case "execSummary":
      return {
        id,
        type: "execSummary",
        title: "Executive Summary",
        metrics: SUMMARY_METRICS,
        filters,
      };
    default:
      return { id, type: "narrative", text: "" } as NarrativeBlock;
  }
}

function newElementDefaults(
  type: ReportBlock["type"],
  filters: ReportFilters,
): CanvasElement {
  const id = newId();
  const block = makeCanvasChildBlock(type, filters, id);
  const isKpi = type === "kpiCards";
  return {
    id,
    x: 40,
    y: 40,
    w: isKpi ? 560 : 580,
    h: type === "chart" ? 280 : 300,
    block,
  };
}

const BLOCK_TYPE_LABELS: Record<string, string> = {
  chart: "Chart",
  kpiCards: "KPI cards",
  breakdown: "Breakdown",
  segmentComparison: "Segment comparison",
  comparison: "Metric comparison",
  weatherImpact: "Weather impact",
  heading: "Heading",
  narrative: "Narrative",
  image: "Image",
  execSummary: "Executive summary",
};

export const CanvasSlideEditor: React.FC<CanvasSlideEditorProps> = ({
  block,
  clientId,
  open,
  onClose,
  onChange,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      const available = containerRef.current.getBoundingClientRect();
      setScale(
        Math.min(available.width / CANVAS_W, available.height / CANVAS_H, 1),
      );
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [open]);

  const updateElement = (id: string, patch: Partial<CanvasElement>) => {
    onChange({
      ...block,
      elements: block.elements.map((e) =>
        e.id === id ? { ...e, ...patch } : e,
      ),
    });
  };

  const deleteElement = (id: string) => {
    onChange({
      ...block,
      elements: block.elements.filter((e) => e.id !== id),
    });
    if (selectedId === id) setSelectedId(null);
  };

  const addElement = (type: ReportBlock["type"]) => {
    const inheritedFilters =
      (
        block.elements.find((e) => "filters" in e.block)?.block as
          | { filters?: ReportFilters }
          | undefined
      )?.filters ?? {};
    const el = newElementDefaults(type, inheritedFilters);
    // Offset each new element so they don't all stack at 40,40.
    const offset = (block.elements.length % 8) * 30;
    el.x = 40 + offset;
    el.y = 40 + offset;
    onChange({ ...block, elements: [...block.elements, el] });
    setAddMenuAnchor(null);
    setSelectedId(el.id);
  };

  const selectedEl = selectedId
    ? block.elements.find((e) => e.id === selectedId) ?? null
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: "#111", display: "flex", flexDirection: "column" } }}
    >
      {/* Toolbar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1, borderBottom: "1px solid rgba(255,255,255,0.1)", flexShrink: 0 }}
      >
        <Typography variant="h6" sx={{ color: "#fff" }}>
          {block.title ?? "Canvas slide"}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.3)" }}
          >
            Add element
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onClose}
          >
            Done
          </Button>
        </Stack>
      </Stack>

      {/* Add element menu */}
      <Menu
        anchorEl={addMenuAnchor}
        open={!!addMenuAnchor}
        onClose={() => setAddMenuAnchor(null)}
        PaperProps={{ sx: { maxHeight: 400 } }}
      >
        <ListSubheader>Data</ListSubheader>
        <MenuItem onClick={() => addElement("chart")}>Chart</MenuItem>
        <MenuItem onClick={() => addElement("kpiCards")}>KPI cards</MenuItem>
        <MenuItem onClick={() => addElement("breakdown")}>Breakdown</MenuItem>
        <MenuItem onClick={() => addElement("segmentComparison")}>Segment comparison</MenuItem>
        <MenuItem onClick={() => addElement("comparison")}>Metric comparison</MenuItem>
        <MenuItem onClick={() => addElement("weatherImpact")}>Weather impact</MenuItem>
        <Divider />
        <ListSubheader>Content</ListSubheader>
        <MenuItem onClick={() => addElement("heading")}>Heading</MenuItem>
        <MenuItem onClick={() => addElement("narrative")}>Narrative</MenuItem>
        <MenuItem onClick={() => addElement("image")}>Image</MenuItem>
      </Menu>

      {/* Main area */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Canvas area */}
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            background: "#111",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedId(null);
          }}
        >
          {/* Outer wrapper holds the scaled dimensions so layout is correct */}
          <div
            style={{
              width: CANVAS_W * scale,
              height: CANVAS_H * scale,
              flexShrink: 0,
              position: "relative",
            }}
          >
            {/* Inner div is the true 1280×720 canvas, scaled from top-left */}
            <div
              style={{
                width: CANVAS_W,
                height: CANVAS_H,
                position: "absolute",
                top: 0,
                left: 0,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                background: CANVAS_BG,
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setSelectedId(null);
              }}
            >
              {block.elements.map((el) => (
                <CanvasElementItem
                  key={el.id}
                  el={el}
                  selected={selectedId === el.id}
                  clientId={clientId}
                  scale={scale}
                  onSelect={() => setSelectedId(el.id)}
                  onDelete={() => deleteElement(el.id)}
                  onUpdate={(patch) => updateElement(el.id, patch)}
                />
              ))}
            </div>
          </div>
        </Box>

        {/* Config panel */}
        {selectedEl && (
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              borderLeft: "1px solid rgba(255,255,255,0.1)",
              display: "flex",
              flexDirection: "column",
              bgcolor: "#1a1f26",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.1)" }}
            >
              <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
                {BLOCK_TYPE_LABELS[selectedEl.block.type] ?? selectedEl.block.type}
              </Typography>
              <Button
                size="small"
                color="error"
                onClick={() => deleteElement(selectedEl.id)}
              >
                Remove
              </Button>
            </Stack>
            <Box sx={{ overflowY: "auto", flex: 1, p: 2 }}>
              <BlockEditor
                block={selectedEl.block}
                clientId={clientId}
                onChange={(next) => updateElement(selectedEl.id, { block: next })}
                onRemove={() => deleteElement(selectedEl.id)}
                nested
              />
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

// ─── Per-element draggable/resizable item ────────────────────────────────────

interface CanvasElementItemProps {
  el: CanvasElement;
  selected: boolean;
  clientId: string | null;
  scale: number;
  onSelect: () => void;
  onDelete: () => void;
  onUpdate: (patch: Partial<CanvasElement>) => void;
}

const CanvasElementItem: React.FC<CanvasElementItemProps> = ({
  el,
  selected,
  clientId,
  scale,
  onSelect,
  onDelete,
  onUpdate,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLElement>}
      bounds="parent"
      position={{ x: el.x, y: el.y }}
      scale={scale}
      handle=".drag-handle"
      cancel=".react-resizable-handle"
      onStart={() => console.log("[drag] start", el.id)}
      onDrag={(_e, d) => console.log("[drag] move", d.x, d.y)}
      onStop={(_e, d) => { console.log("[drag] stop", d.x, d.y); onUpdate({ x: d.x, y: d.y }); }}
    >
      <div
        ref={nodeRef}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          outline: selected
            ? "2px solid #2196f3"
            : "1px solid rgba(255,255,255,0.1)",
          cursor: "move",
          background: "rgba(255,255,255,0.03)",
          borderRadius: 4,
          boxSizing: "border-box",
        }}
      >
        {/* Drag handle bar at top — always grabbable */}
        <div
          className="drag-handle"
          style={{
            height: 20,
            background: selected ? "rgba(33,150,243,0.3)" : "rgba(255,255,255,0.06)",
            borderRadius: "4px 4px 0 0",
            cursor: "grab",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            userSelect: "none",
          }}
        >
          <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 9, fontFamily: "monospace" }}>
            ⠿ drag me ⠿ &nbsp; x:{Math.round(el.x)} y:{Math.round(el.y)}
          </span>
        </div>
        <ResizableBox
          width={el.w}
          height={el.h - 20}
          minConstraints={[120, 60]}
          maxConstraints={[CANVAS_W, CANVAS_H - 20]}
          onResizeStop={(_e, { size }) =>
            onUpdate({ w: size.width, h: size.height + 20 })
          }
          resizeHandles={selected ? ["se", "s", "e"] : []}
        >
          <div style={{ width: "100%", height: "100%", overflow: "hidden", pointerEvents: "none" }}>
            <SlideBlockView block={el.block} clientId={clientId} fill />
          </div>
        </ResizableBox>
        {selected && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              position: "absolute",
              top: -12,
              right: -12,
              background: "#f44336",
              color: "#fff",
              width: 24,
              height: 24,
              padding: 0,
            }}
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </IconButton>
        )}
      </div>
    </Draggable>
  );
};
